import React, { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_AFF } from "@/constants/testIds";

export default function AdminCommissionConfig() {
  const { user } = useAuth();
  const isMaster = user?.roles?.includes("master_admin");
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const reload = async () => {
    try { const { data } = await api.get("/admin/affiliate/commission-config"); setCfg(data); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    setBusy(true); setErr(""); setOk(false);
    try {
      await api.put("/admin/affiliate/commission-config", {
        shopify_pct: cfg.shopify_pct,
        lifted_supplies_pct: cfg.lifted_supplies_pct,
        partner_pct: cfg.partner_pct,
        franchise_pct: cfg.franchise_pct,
        tier_multipliers: cfg.tier_multipliers,
      });
      setOk(true); setTimeout(() => setOk(false), 1500);
      await reload();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  if (!cfg) return <div className="text-lifted-dim font-bebas tracking-[0.3em]">LOADING…</div>;

  const updNum = (k) => (e) => setCfg({ ...cfg, [k]: e.target.value === "" ? null : parseFloat(e.target.value) });
  const updMult = (tier) => (e) => setCfg({ ...cfg, tier_multipliers: { ...(cfg.tier_multipliers || {}), [tier]: parseFloat(e.target.value) || 0 } });

  return (
    <div data-testid={ADMIN_AFF.config} className="space-y-6">
      <header className="fade-up">
        <div className="eyebrow">Affiliate admin</div>
        <h1 className="headline-xl">Commission config</h1>
        <p className="text-lifted-dim mt-2 text-sm max-w-2xl">
          Commission percentages live ONLY here. Until set, every attributed order lands as
          <code className="text-lifted-text"> pending_review · $0</code>.
          {!isMaster && <span className="text-rose-300 ml-2">Read-only — master_admin required to mutate.</span>}
        </p>
      </header>

      {err && <div className="text-rose-300 text-sm">{err}</div>}

      <section className="glass-panel p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field testid={ADMIN_AFF.configShopify} label="Shopify %" value={cfg.shopify_pct} onChange={updNum("shopify_pct")} disabled={!isMaster} />
          <Field testid={ADMIN_AFF.configSupplies} label="Lifted Supplies %" value={cfg.lifted_supplies_pct} onChange={updNum("lifted_supplies_pct")} disabled={!isMaster} />
          <Field testid={ADMIN_AFF.configPartner} label="Partner %" value={cfg.partner_pct} onChange={updNum("partner_pct")} disabled={!isMaster} />
          <Field testid={ADMIN_AFF.configFranchise} label="Franchise %" value={cfg.franchise_pct} onChange={updNum("franchise_pct")} disabled={!isMaster} />
        </div>
        <div className="mt-6">
          <div className="eyebrow !text-lifted-text-dim mb-2">Tier multipliers (×)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(cfg.tier_multipliers || {}).map(([tier, mult]) => (
              <div key={tier}>
                <label className="lifted-label">{tier}</label>
                <input type="number" step="0.05" className="input-lifted" value={mult} onChange={updMult(tier)} disabled={!isMaster} />
              </div>
            ))}
          </div>
        </div>
        <button data-testid={ADMIN_AFF.configSave} onClick={save} disabled={busy || !isMaster} className="btn-lifted mt-6 !text-[0.7rem] disabled:opacity-40">
          {busy ? "Saving…" : ok ? "Saved" : "Save config"}
        </button>
      </section>
    </div>
  );
}

function Field({ testid, label, value, onChange, disabled }) {
  return (
    <div>
      <label className="lifted-label">{label}</label>
      <input data-testid={testid} type="number" step="0.1" className="input-lifted" value={value ?? ""} onChange={onChange} placeholder="not configured" disabled={disabled} />
    </div>
  );
}
